import React from "react";
import { Box, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { TruncatedTextWithTooltip } from "../../components/ui/TruncatedTextWithTooltip";
import greenCheckVerified from "../../assets/svgIcons/green-check-verified.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// --- Interfaces ---
export interface DetailItem {
  label: string;
  value?: string | number | boolean | React.ReactNode;
  isVerified?: boolean;
}

interface DetailRowProps {
  item: DetailItem;
  showInfoIcon?: boolean;
  salespersonTooltipContent?: any;
}

const DetailRow: React.FC<DetailRowProps> = ({
  item,
  showInfoIcon,
  salespersonTooltipContent,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const { label, value, isVerified } = item;
  let displayValue: React.ReactNode = value;

  // 1. Define keywords for detection
  const currencyKeywords = [
    "amount",
    "price",
    "fees",
    "cost",
    "charge",
    "balance",
    "expenses",
    "income",
    "obligation",
    "own sources",
    "collateral value",
    "expense",
    "totalvalue",
    "value",
  ];

  const isCurrencyField = currencyKeywords.some((keyword) =>
    label?.toLowerCase().includes(keyword)
  );

  // NEW: Check if the label refers to a PAN card
  const isPanField = label?.toLowerCase().includes("pan");

  // 2. Clean the value for numeric checking
  const cleanValue =
    typeof value === "string"
      ? value.replace(/[₹\u00A0,]/g, "").trim()
      : value;

  // 3. Validation Logic
  const hasActualData = value !== null && value !== "" && value !== "-";
  const isValidNumber =
    !isNaN(Number(cleanValue)) && isFinite(Number(cleanValue));

  // 4. Formatting Logic
  if (isPanField && typeof value === "string" && value.length === 10) {
    /**
     * PAN Masking Logic:
     * Takes first 5 chars, replaces next 4 with asterisks, keeps the last char.
     * Example: ABCDE1234F -> ABCDE****F
     */
    displayValue = value.toUpperCase().replace(/^(.{5}).{4}(.{1})$/, "$1****$2");
  } else if (isCurrencyField && hasActualData && isValidNumber) {
    displayValue = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
      .format(Number(cleanValue))
      .replace("₹", "₹\u00A0");
  } else {
    displayValue = value || "-";
  }

  // 5. Determine Text Transform logic
  const getTextTransform = () => {
    if (isPanField) return "uppercase"; // Keep PAN format intact
    if (label?.toLowerCase().includes("email")) return "lowercase";
    return "capitalize";
  };

  return (
    <Box className="card-contaner-section">
      {/* Label Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <Typography
          style={{
            color: "#a5a3ae",
            fontFamily: "Jost",
            fontWeight: "400",
            fontSize: "13px",
            lineHeight: "14px",
            textTransform: "capitalize",
            marginRight: showInfoIcon ? "4px" : "0",
          }}
        >
          {label}
        </Typography>

        {showInfoIcon && (
          <Tooltip
            title={salespersonTooltipContent}
            placement="top-start"
            arrow
          >
            <IconButton
              size="small"
              sx={{
                padding: 0,
                "&:hover": { background: "transparent" },
              }}
              aria-label="Salesperson contact details"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: "14px",
                  color: "#a5a3ae",
                  cursor: "pointer",
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Value Section */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {!isCurrencyField &&
        !isPanField &&
        typeof displayValue === "string" &&
        isNaN(Number(cleanValue)) ? (
          <TruncatedTextWithTooltip
            style={{
              color: "#6f6b7d",
              fontFamily: "Jost",
              fontWeight: "700",
              fontSize: "12px",
              lineHeight: "20px",
              textTransform: getTextTransform(),
            }}
            text={String(displayValue || "")}
            // limit={35}
          />
        ) : (
          <Typography
            style={{
              color: "#6f6b7d",
              fontFamily: "Jost",
              fontWeight: "700",
              fontSize: "12px",
              lineHeight: "20px",
              textTransform: getTextTransform(),
            }}
          >
            {displayValue}
          </Typography>
        )}

        {isVerified && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#28C76F", fontSize: "12px", }}>
            <img
              src={greenCheckVerified}
              alt="Verified"
              style={{ height: '12px', objectFit: 'contain', imageRendering: '-webkit-optimize-contrast' }}
            />
            <Typography variant="inherit">Verified</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DetailRow;
